import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Instagram } from 'lucide-react';
import { contactInfo, navLinks } from '../data/mock';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import { localizedPath, parsePathname, sectionId } from '../utils/routes';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const currentLang = lang || 'it';

  const translatedNavLinks = navLinks.map(link => ({
    ...link,
    label: t(`nav.${link.id}`)
  }));

  const smoothScrollTo = (targetId) => {
    const el = document.getElementById(targetId);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const handleNavClick = (link) => {
    if (link.section) {
      const { page: currentPage } = parsePathname(location.pathname);
      const targetId = sectionId(currentLang, link.section);
      if (currentPage !== 'home') {
        navigate(localizedPath(currentLang, 'home'));
        setTimeout(() => smoothScrollTo(targetId), 500);
      } else {
        smoothScrollTo(targetId);
      }
    } else {
      navigate(localizedPath(currentLang, link.page));
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-black relative">
      {/* Top border line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/30 to-transparent" />
      
      {/* Main footer content */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-16">
            {/* Brand */}
            <div className="lg:col-span-4">
              <Link to={localizedPath(currentLang, 'home')} className="block mb-8">
                <h3 className="font-display text-4xl text-white">
                  DOUBLE <span className="text-[#FFD700]">A</span> STUDIO
                </h3>
              </Link>
              <p className="text-white/70 font-light leading-relaxed mb-8">
                {t('footer.tagline')}
              </p>
              <motion.button
                onClick={scrollToTop}
                className="text-[#FFD700] text-xs tracking-[0.3em] uppercase flex items-center gap-3 group"
                whileHover={{ y: -3 }}
              >
                <span>{t('footer.back_to_top')}</span>
                <svg className="w-4 h-4 group-hover:-translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </motion.button>
            </div>

            {/* Navigation */}
            <div className="lg:col-span-3 lg:col-start-6">
              <h4 className="text-white/60 text-xs tracking-[0.3em] uppercase mb-8">{t('footer.navigation')}</h4>
              <ul className="space-y-4">
                {translatedNavLinks.map((link) => (
                  <li key={link.id}>
                    <motion.button
                      onClick={() => handleNavClick(link)}
                      className="text-white/70 hover:text-[#FFD700] transition-colors duration-300 text-sm"
                      whileHover={{ x: 5 }}
                    >
                      {link.label}
                    </motion.button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="lg:col-span-3">
              <h4 className="text-white/60 text-xs tracking-[0.3em] uppercase mb-8">{t('footer.contacts')}</h4>
              <ul className="space-y-4">
                <li>
                  <a 
                    href={`mailto:${contactInfo.email}`}
                    className="text-white/70 hover:text-[#FFD700] transition-colors duration-300 text-sm"
                  >
                    {contactInfo.email}
                  </a>
                </li>
                <li>
                  <a 
                    href={contactInfo.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/70 hover:text-[#FFD700] transition-colors duration-300 text-sm inline-flex items-center gap-2"
                    data-testid="footer-instagram-link"
                  >
                    <Instagram className="w-4 h-4" />
                    Instagram
                  </a>
                </li>
                <li>
                  <span className="text-white/70 text-sm">{contactInfo.location}</span>
                </li>
              </ul>
              
              {/* Language selector in footer */}
              <div className="mt-8">
                <LanguageSelector variant="footer" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 relative z-10">
            <p className="text-white/50 text-sm">
              &copy; {currentYear} Double A Studio. {t('footer.copyright')}
            </p>
            <p className="text-white/40 text-xs tracking-wider">
              CHE-208.371.352
            </p>
            <p className="text-white/40 text-xs tracking-wider">
              {t('footer.made_in')}
            </p>
          </div>
        </div>
      </div>
      
      {/* Large background text */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
        <motion.h2 
          className="font-display text-[20vw] text-white/[0.02] leading-none whitespace-nowrap"
          animate={{ x: [0, -1000] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        >
          DOUBLE A STUDIO &bull; DOUBLE A STUDIO &bull; DOUBLE A STUDIO &bull;
        </motion.h2>
      </div>
    </footer>
  );
};

export default Footer;
